/**
 * SAD-105: the 14-day deliverable lookahead, on the contract model (D112 #6).
 * One ListOccurrences over every window for the dates still owed that are
 * late or due in the next 14 days (occurrencestatus' `due_soon` IS the
 * 14-day horizon, so the window is pangaea's, never re-derived here). Late
 * dates lead the list: a lookahead that hid them would read as "all clear".
 * Dates projected from an event not logged yet (S6) come in under the same
 * horizon (due_to), marked projected and never late.
 *
 * One component, mounted on program home and as its own program section
 * beside Weekly Recap — do not fork a second copy.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraLink, SpecraPanelHeader, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { EmptyState } from '../../../pages/Delivery/_shared/components/PageStates';
import { CmAwaitingApproval, CmStatusBadge, CmTable } from '../../../pages/Delivery/Deliverables/components/common';
import { OccurrenceWindow, useDuties, useOccurrences, useWorkstreams } from '../../../pages/Delivery/Deliverables/hooks';
import { addDays, cmDate, todayISO } from '../../../pages/Delivery/Deliverables/lib/format';
import { occurrenceStatus } from '../../../pages/Delivery/Deliverables/lib/status';
const LOOKAHEAD_STATES = [
    'late',
    'due_soon',
    'waiting_on_event'
];
// occurrencestatus.DefaultDueSoonDays, the horizon due_soon already means.
const HORIZON_DAYS = 14;
// Late first, then soonest due; ties by id so a refetch never reshuffles rows.
function byUrgency(a, b) {
    const al = a.status?.state === 'late' ? 0 : 1;
    const bl = b.status?.state === 'late' ? 0 : 1;
    if (al !== bl) return al - bl;
    return (a.dueOn || '9999').localeCompare(b.dueOn || '9999') || a.id.localeCompare(b.id);
}
export function DeliverableLookaheadList({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const occ = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        states: LOOKAHEAD_STATES,
        includeProjected: true,
        // Late and due-soon rows all fall on or before it; it bounds the projected ones.
        dueTo: addDays(todayISO(), HORIZON_DAYS)
    });
    // Same cache entries useCmLookups reads, without its period/contact reads.
    const duties = useDuties(programId, {
        includeRules: true
    });
    const workstreams = useWorkstreams(programId);
    const dutyById = new Map((duties.data?.duties ?? []).map((d)=>[
            d.id,
            d
        ]));
    const teamById = new Map((workstreams.data ?? []).map((w)=>[
            w.id,
            w.name
        ]));
    const rows = [
        ...occ.data?.occurrences ?? []
    ].sort(byUrgency);
    const columns = [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (o)=>{
                const duty = dutyById.get(o.dutyId);
                return /*#__PURE__*/ _jsxs(SpecraLink, {
                    href: `${base}/deliverables/${o.dutyId}`,
                    renderLink: renderRouterLink,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: duty?.title || 'Untitled deliverable'
                        }),
                        o.label ? /*#__PURE__*/ _jsx(SpecraText, {
                            className: "ml-1.5",
                            tone: "muted",
                            children: o.label
                        }) : null
                    ]
                });
            }
        },
        {
            id: 'team',
            header: 'Team',
            cell: (o)=>o.workstreamId ? teamById.get(o.workstreamId) ?? 'Unknown team' : 'Contract-wide'
        },
        {
            id: 'due',
            header: 'Due',
            cell: (o)=>o.event?.projected ? /*#__PURE__*/ _jsxs("span", {
                    className: "tabular-nums",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraLink, {
                            href: `${base}/deliverables/${o.dutyId}`,
                            renderLink: renderRouterLink,
                            children: cmDate(o.dueOn)
                        }),
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "info",
                            className: "ml-2",
                            "data-testid": Testids.cmDuty.projectedBadge,
                            children: "Projected"
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraLink, {
                    className: "tabular-nums",
                    href: `${base}/deliverables?occ=${o.id}`,
                    renderLink: renderRouterLink,
                    children: cmDate(o.dueOn)
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-1",
                    children: [
                        /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: occurrenceStatus(o, dutyById.get(o.dutyId)?.finalReviewTier)
                        }),
                        /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                            programId: programId,
                            occurrenceId: o.id
                        })
                    ]
                })
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.deliverableLookahead.section,
        children: [
            /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                subtitle: "Deliverables due in the next 14 days, and any already late.",
                title: "Next 14 Days"
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Deliverables due in the next 14 days",
                rows: rows,
                columns: columns,
                rowKey: (o)=>o.id,
                testId: Testids.deliverableLookahead.table,
                rowTestId: Testids.deliverableLookahead.row,
                isLoading: occ.isLoading,
                error: occ.error,
                onRetry: ()=>void occ.refetch(),
                empty: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.deliverableLookahead.empty,
                    children: /*#__PURE__*/ _jsx(EmptyState, {
                        title: "Nothing due in the next 14 days",
                        action: /*#__PURE__*/ _jsx(Link, {
                            to: `${base}/deliverables`,
                            className: specraButtonVariants({
                                variant: 'secondary',
                                size: 'sm'
                            }),
                            children: "Open Deliverables"
                        })
                    })
                })
            })
        ]
    });
}
